import { adminFetch, API_BASE } from "./_client";
import type { CommsEmailDef, CommsSmsDef, CommsPushDef, CommsFlowDef, CommsCronJobDef, CommsOrphanEntry, CommsCronMapping, CommsNotifPref, CommsDashboard, CommsUserPrefs } from "./types";


export function fetchCommsDashboard(token: string) {
  return adminFetch<CommsDashboard>("/admin/communications/dashboard", token);
}


export function fetchCommsEmails(token: string, audience?: string) {
  const qs = audience && audience !== "all" ? `?audience=${audience}` : "";
  return adminFetch<{ emails: CommsEmailDef[]; total: number }>(`/admin/communications/emails${qs}`, token);
}


export function fetchCommsEmailById(token: string, id: string) {
  return adminFetch<CommsEmailDef>(`/admin/communications/emails/${id}`, token);
}


export function fetchCommsSms(token: string, audience?: string) {
  const qs = audience && audience !== "all" ? `?audience=${audience}` : "";
  return adminFetch<{ sms: CommsSmsDef[]; total: number }>(`/admin/communications/sms${qs}`, token);
}


export function fetchCommsPush(token: string, audience?: string) {
  const qs = audience && audience !== "all" ? `?audience=${audience}` : "";
  return adminFetch<{ push: CommsPushDef[]; total: number }>(`/admin/communications/push${qs}`, token);
}


export function fetchCommsFlows(token: string, category?: string) {
  const qs = category && category !== "all" ? `?category=${category}` : "";
  return adminFetch<{ flows: CommsFlowDef[]; total: number }>(`/admin/communications/flows${qs}`, token);
}


export function fetchCommsFlowDetail(token: string, id: string) {
  return adminFetch<{ flow: CommsFlowDef; email: CommsEmailDef | null; sms: CommsSmsDef | null; push: CommsPushDef | null }>(`/admin/communications/flows/${id}`, token);
}


export function fetchCommsCronJobs(token: string) {
  return adminFetch<{ cronJobs: CommsCronJobDef[] }>("/admin/communications/cron-jobs", token);
}


export function fetchCommsOrphans(token: string, channel: "all" | "email" | "sms" | "push" = "all") {
  const qs = channel !== "all" ? `?channel=${channel}` : "";
  return adminFetch<{ orphans: CommsOrphanEntry[]; total: number; totals: { all: number; email: number; sms: number; push: number } }>(`/admin/communications/orphans${qs}`, token);
}


export function fetchCommsCronMappings(token: string) {
  return adminFetch<{ mappings: CommsCronMapping[] }>("/admin/communications/cron-mappings", token);
}


export function sendCommsTestEmail(token: string, emailId: string, toAddress: string) {
  return adminFetch<{ success: boolean; message: string }>("/admin/communications/test-email", token, {
    method: "POST",
    body: JSON.stringify({ emailId, toAddress }),
  });
}


export function sendCommsTestSms(token: string, smsId: string, toPhone: string) {
  return adminFetch<{ success: boolean; message: string }>("/admin/communications/test-sms", token, {
    method: "POST",
    body: JSON.stringify({ smsId, toPhone }),
  });
}


export function getCommsExportUrl(type: "emails-csv" | "emails-zip" | "sms-csv" | "push-json" | "flows-json" | "matrix-csv") {
  return `${API_BASE}/admin/communications/export/${type}`;
}


export function fetchCommsEmailPreview(
  token: string,
  emailId: string,
  trigger?: string,
  opts?: { brand?: string; locale?: string },
) {
  const params = new URLSearchParams();
  if (trigger) params.set("trigger", trigger);
  if (opts?.brand) params.set("brand", opts.brand);
  if (opts?.locale) params.set("locale", opts.locale);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return adminFetch<{ html: string; emailId: string; name: string; subject?: string }>(`/admin/communications/emails/${emailId}/preview${qs}`, token);
}


export function fetchCommsNotifPrefs(token: string) {
  return adminFetch<{ preferences: CommsNotifPref[]; totalUsers: number }>("/admin/communications/notification-preferences", token);
}


export function fetchCommsUserPrefs(token: string, search?: string, page?: number) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (page && page > 1) params.set("page", String(page));
  const qs = params.toString() ? `?${params}` : "";
  return adminFetch<{ users: CommsUserPrefs[]; total: number; page: number; limit: number }>(`/admin/communications/notification-preferences/users${qs}`, token);
}
